import IconSuccessSolid from '@apollo/icons/default/IconSuccessSolid.svg';
import IconWarningSolid from '@apollo/icons/default/IconWarningSolid.svg';
import { Spinner } from '@apollo/orbit';
import React from 'react';

import { IntrospectionFailure } from 'src/app/graph/explorerPage/hooks/useExplorerState/useSchema/useSchemaFromEndpointIntrospection/useSchemaFromEndpointIntrospection';
import { format } from 'src/lib/format';

export const getIntrospectionHelper = ({
  schemaUploaded,
  introspectionFailureType,
  isUsingLocalIntrospection,
  isLoading,
  introspectionResponseErrors,
  sdlFromIntrospection,
  subgraphSdlFromIntrospection,
  routingUrl,
}: {
  schemaUploaded: boolean;
  introspectionFailureType: IntrospectionFailure | undefined;
  isUsingLocalIntrospection: boolean;
  isLoading: boolean;
  introspectionResponseErrors:
    | {
        message: string;
      }[]
    | undefined;
  sdlFromIntrospection: string | undefined;
  subgraphSdlFromIntrospection: string | undefined;
  routingUrl: string;
}) => {
  if (
    schemaUploaded &&
    (introspectionFailureType === 'introspectionDisabled' ||
      introspectionFailureType === 'invalidIntrospectionResponse' ||
      introspectionFailureType === 'invalidIntrospectionSchema' ||
      !introspectionFailureType)
  ) {
    return {
      type: 'helper',
      icon: <IconSuccessSolid className="mr-2 mt-0.5 text-icon-success" />,
      message: `We are using your ${
        isUsingLocalIntrospection ? 'introspected' : 'uploaded'
      } schema!`,
    };
  } else if (
    schemaUploaded &&
    introspectionFailureType === 'noEndpointProvided'
  ) {
    return {
      type: 'error',
      message: 'We have your schema, but we need an endpoint to proceed!',
    };
  } else if (isLoading && routingUrl.length > 0) {
    return {
      type: 'helper',
      message: 'Attempting to fetch the schema from your GraphQL API endpoint.',
      icon: <Spinner size="tiny" className="mr-2" />,
    };
  } else if (introspectionFailureType === 'unableToReachEndpoint') {
    return {
      type: 'error',
      message: `${
        schemaUploaded
          ? 'We are using your uploaded schema, however we need a reachable endpoint to proceed.'
          : ''
      } We couldn't connect to that URL. Please double-check that it's correct.`,
    };
  } else if (
    introspectionFailureType === 'invalidIntrospectionResponse' ||
    introspectionFailureType === 'invalidIntrospectionSchema'
  ) {
    return {
      type: 'error',
      message:
        'We got an invalid introspection response from this endpoint. Please check the URL or add required headers below.',
    };
  } else if (!introspectionFailureType && sdlFromIntrospection) {
    return {
      type: 'helper',
      icon: <IconSuccessSolid className="mr-2 mt-0.5 text-icon-success" />,
      message: `Connection succeeded and we have your${
        subgraphSdlFromIntrospection ? ' subgraph' : ''
      } schema!`,
    };
  } else if (introspectionFailureType === 'introspectionDisabled') {
    return {
      type: 'helper',
      icon: (
        <IconWarningSolid className="mr-2 mt-0.5 size-4 text-icon-warning" />
      ),
      message: (
        <>
          Connection succeeded, but we didn't receive your schema! Please
          resolve the following{' '}
          {format.pluralize(introspectionResponseErrors?.length, 'error')}:{' '}
          {introspectionResponseErrors?.length ? (
            <>
              <span className="italic">
                "{introspectionResponseErrors.map((error) => error.message)}"
              </span>{' '}
              or directly upload your schema below.
            </>
          ) : (
            <p className="font-semibold">Please upload it below.</p>
          )}
        </>
      ),
    };
  }

  return undefined;
};
